import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Chip,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Avatar,
  Badge,
  CircularProgress,
  Fade,
  Slide,
  Grow,
  Zoom,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as WalletIcon,
  Settings as SettingsIcon,
  Error as AlertCircleIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  AttachMoney as DollarSignIcon,
  FlashOn as ZapIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { getBackendUrl } from '../config/urls';

const MEV = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mevStatus, setMevStatus] = useState({
    isRunning: false,
    startTime: null,
    totalOpportunities: 0,
    totalProfit: '0',
    successfulBundles: 0
  });
  
  const [opportunities, setOpportunities] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [targetWallets, setTargetWallets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  
  // MEV Bot configuration
  const [config, setConfig] = useState({
    privateKey: '',
    receiverAddress: '',
    maxGasPrice: '500',
    strategies: {
      autoSwap: true
    }
  });

  const BACKEND_URL = getBackendUrl();
  const token = localStorage.getItem('token');

  // Fetch MEV Bot status
  const fetchMevStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mev/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setMevStatus(data);
    } catch (error) {
      console.error('Error fetching MEV status:', error);
    }
  };

  // Fetch opportunities
  const fetchOpportunities = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mev/opportunities?limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setOpportunities(data);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    }
  };

  // Fetch bundles
  const fetchBundles = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mev/bundles?limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setBundles(data);
    } catch (error) {
      console.error('Error fetching bundles:', error);
    }
  };

  // Fetch target wallets
  const fetchTargetWallets = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mev/target-wallets`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setTargetWallets(data);
    } catch (error) {
      console.error('Error fetching target wallets:', error);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mev/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch configuration
  const fetchConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mev/config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setConfig(prevConfig => ({
        ...prevConfig,
        privateKey: data.privateKey || '',
        receiverAddress: data.receiverAddress || '',
        maxGasPrice: data.maxGasPrice || '500',
        strategies: data.strategies || { autoSwap: true }
      }));
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  // Start MEV Bot
  const startMevBot = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${BACKEND_URL}/api/mev/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          privateKey: config.privateKey,
          chainKey: 'sepolia'
        })
      });

      const data = await response.json();

      if (data.success) {
        setMevStatus(data.status);
        await fetchMevStatus();
      } else {
        setError(data.error || 'Failed to start MEV Bot');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Stop MEV Bot
  const stopMevBot = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${BACKEND_URL}/api/mev/stop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setMevStatus(data.status);
      } else {
        setError(data.error || 'Failed to stop MEV Bot');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Update configuration
  const updateConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${BACKEND_URL}/api/mev/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          privateKey: config.privateKey,
          receiverAddress: config.receiverAddress,
          maxGasPrice: config.maxGasPrice,
          strategies: config.strategies
        })
      });

      const data = await response.json();

      if (data.success) {
        // Show success message
        console.log('Configuration updated successfully');
      } else {
        setError(data.error || 'Failed to update configuration');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  // Load data on component mount
  useEffect(() => {
    fetchMevStatus();
    fetchOpportunities();
    fetchBundles();
    fetchTargetWallets();
    fetchStats();
    fetchConfig();
  }, []);

  // Derive wallet address from private key
  useEffect(() => {
    if (config.privateKey) {
      try {
        // Import ethers dynamically
        import('ethers').then(({ ethers }) => {
          const wallet = new ethers.Wallet(config.privateKey);
          setWalletAddress(wallet.address);
        }).catch(() => {
          setWalletAddress('Invalid private key');
        });
      } catch (error) {
        setWalletAddress('Invalid private key');
      }
    } else {
      setWalletAddress('');
    }
  }, [config.privateKey]);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, sm: 2.5, md: 3 } }}>
      <Card sx={{ 
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <CardHeader
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            gap: { xs: 1, sm: 0 },
            pb: { xs: 1, sm: 2 },
            '& .MuiCardHeader-action': {
              m: 0,
              alignSelf: { xs: 'stretch', sm: 'auto' },
              width: { xs: '100%', sm: 'auto' },
            },
          }}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <TrendingUpIcon sx={{ color: 'primary.main', fontSize: { xs: 24, sm: 28 } }} />
              <Typography variant="h4" fontWeight={600} sx={{ fontSize: { xs: '1.35rem', sm: '2rem' } }}>
                MEV Bot
              </Typography>
              <Chip
                label={mevStatus.isRunning ? "Running" : "Stopped"}
                color={mevStatus.isRunning ? "success" : "default"}
                size="small"
                sx={{ ml: { xs: 0, sm: 1 } }}
              />
            </Box>
          }
          subheader="Monitor and protect your wallets from incoming token transactions"
          action={
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' }, mt: { xs: 1, md: 0 } }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<PlayIcon />}
                onClick={startMevBot}
                disabled={loading || mevStatus.isRunning}
                size="small"
              >
                Start
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<StopIcon />}
                onClick={stopMevBot}
                disabled={loading || !mevStatus.isRunning}
                size="small"
              >
                Stop
              </Button>
              <IconButton
                onClick={() => {
                  fetchMevStatus();
                  fetchOpportunities();
                  fetchBundles();
                  fetchTargetWallets();
                  fetchStats();
                }}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mx: { xs: 2, sm: 3 }, mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <LinearProgress sx={{ mx: { xs: 2, sm: 3 }, mb: 2 }} />
        )}

        <CardContent sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44 } }}
          >
            <Tab label="Overview" />
            <Tab label="Opportunities" />
            <Tab label="Bundles" />
            <Tab label="Target Wallets" />
            <Tab label="Configuration" />
          </Tabs>

          {currentTab === 0 && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Card sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary" fontWeight={600}>
                      {mevStatus.totalOpportunities || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Opportunities Detected
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main" fontWeight={600}>
                      {mevStatus.successfulBundles || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Successful Bundles
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main" fontWeight={600}>
                      {mevStatus.totalProfit || '0'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Profit (ETH)
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main" fontWeight={600}>
                      {targetWallets.length || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Target Wallets
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              {mevStatus.startTime && (
                <Card sx={{ mt: 3, p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Bot Status
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Started: {formatTimestamp(mevStatus.startTime)}
                  </Typography>
                </Card>
              )}
            </Box>
          )}

          {currentTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Recent Opportunities
              </Typography>
              {Array.isArray(opportunities) && opportunities.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Target Wallet</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Strategy</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {opportunities.map((opportunity) => (
                        <TableRow key={opportunity.id}>
                          <TableCell>{opportunity.target_wallet}</TableCell>
                          <TableCell>{opportunity.target_method}</TableCell>
                          <TableCell>{opportunity.strategy}</TableCell>
                          <TableCell>
                            <Chip
                              label={opportunity.status}
                              color={opportunity.status === 'included' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatTimestamp(opportunity.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No opportunities detected yet.
                </Alert>
              )}
            </Box>
          )}

          {currentTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Flashbots Bundles
              </Typography>
              {Array.isArray(bundles) && bundles.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Bundle Hash</TableCell>
                        <TableCell>Strategy</TableCell>
                        <TableCell>Profit</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bundles.map((bundle) => (
                        <TableRow key={bundle.id}>
                          <TableCell>{bundle.bundle_hash}</TableCell>
                          <TableCell>{bundle.strategy}</TableCell>
                          <TableCell>{bundle.profit_actual || bundle.profit_estimated}</TableCell>
                          <TableCell>
                            <Chip
                              label={bundle.status}
                              color={bundle.status === 'included' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatTimestamp(bundle.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No bundles created yet.
                </Alert>
              )}
            </Box>
          )}

          {currentTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Target Wallets (All Wallets)
              </Typography>
              {Array.isArray(targetWallets) && targetWallets.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Added</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {targetWallets.map((wallet) => (
                        <TableRow key={wallet.id}>
                          <TableCell>{wallet.name}</TableCell>
                          <TableCell>{wallet.address}</TableCell>
                          <TableCell>
                            <Chip
                              icon={wallet.is_delegated ? <CheckCircleIcon /> : <WalletIcon />}
                              label={wallet.is_delegated ? "Delegated" : "Regular"}
                              color={wallet.is_delegated ? "success" : "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatTimestamp(wallet.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No wallets found in database.
                </Alert>
              )}
            </Box>
          )}

          {currentTab === 4 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                MEV Bot Configuration
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Current Configuration:</strong> The form below shows your saved configuration. 
                  Update any fields and click "Update Configuration" to save changes.
                </Typography>
              </Alert>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="MEV Bot Private Key"
                    type={showPrivateKey ? "text" : "password"}
                    value={config.privateKey}
                    onChange={(e) => setConfig({...config, privateKey: e.target.value})}
                    helperText="Private key for MEV bot wallet (gas sponsor)"
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowPrivateKey(!showPrivateKey)}
                          edge="end"
                        >
                          {showPrivateKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      ),
                    }}
                  />
                  {config.privateKey && (
                    <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Wallet Address:</strong>
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                        {walletAddress || 'Deriving address...'}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Receiver Address (WETH Destination)"
                    value={config.receiverAddress}
                    onChange={(e) => setConfig({...config, receiverAddress: e.target.value})}
                    helperText="Address to receive converted WETH"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Maximum Gas Price (Gwei)"
                    type="number"
                    value={config.maxGasPrice}
                    onChange={(e) => setConfig({...config, maxGasPrice: e.target.value})}
                    helperText="Maximum gas price for fast execution (higher = faster)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.strategies.autoSwap}
                        onChange={(e) => setConfig({
                          ...config, 
                          strategies: {...config.strategies, autoSwap: e.target.checked}
                        })}
                      />
                    }
                    label="Auto Swap Tokens to WETH"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                    Automatically swap ANY incoming tokens to WETH and send to receiver address (no minimum amount)
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={updateConfig}
                    size="large"
                  >
                    Update Configuration
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default MEV; 